// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a message sent while the agent works does (JP 2026-10-30). Steer (the default) adds it
// to the running turn; Queue sends it once the turn ends. Enter does the chosen one and ⌘/Ctrl+Enter
// the other. Settings → Connections → Agents, under the machines; saved in the Agents prefs.

import {ChoiceRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import {modifierKey} from '@app/features/konvo_agents/components/AgentsCopy';
import Agents from '@app/features/konvo_agents/state/Agents';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {useLingui} from '@lingui/react/macro';
import {IconCornerDownLeft} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

type FollowUp = 'steer' | 'queue';

export const AgentFollowUpSettings = observer(() => {
	const {t} = useLingui();
	useEffect(() => {
		if (!Agents.prefs) void Agents.loadPrefs();
	}, []);
	const value = Agents.followUp;
	const mod = modifierKey();
	const keys = (choice: FollowUp) =>
		choice === value
			? t({
					message: 'Enter',
					comment: 'Agents Settings: the key that sends a message this way (the chosen default).',
				})
			: t({
					message: `${mod} Enter`,
					comment: 'Agents Settings: the keys that send a message this way (not the default). The first is ⌘ or Ctrl.',
				});
	const steerKeys = keys('steer');
	const queueKeys = keys('queue');
	const steer = t({message: 'Steer', comment: 'Agents Settings: option that adds the message to the running turn.'});
	const queue = t({
		message: 'Queue',
		comment: 'Agents Settings: option that sends the message after the running turn.',
	});
	const pick = (next: FollowUp) =>
		void Agents.savePrefs({followUp: next}).catch(() =>
			ToastCommands.error(
				t({message: "Couldn't save that. Try again.", comment: 'Agents Settings: saving the choice failed.'}),
			),
		);
	return (
		<ChoiceRow
			icon={<IconCornerDownLeft size={18} stroke={1.7} />}
			title={t({
				message: 'Enter while an agent works',
				comment: 'Settings → Agents: the choice of what Enter does while an agent is working.',
			})}
			description={
				value === 'steer'
					? t({
							message: 'Your message joins the running turn',
							comment: 'Settings → Agents: what Steer does, in short.',
						})
					: t({
							message: 'Your message waits for the turn to end',
							comment: 'Settings → Agents: what Queue does, in short.',
						})
			}
			value={value === 'steer' ? steer : queue}
			disabled={!Agents.prefs}
			groups={(onClose) => [
				{
					items: [
						{
							label: steer,
							subtext: t({
								message: `Changes course straight away. ${steerKeys}.`,
								comment:
									'Agents Settings: what Steer does. The last part is the key that does it: Enter, or ⌘/Ctrl Enter.',
							}),
							selected: value === 'steer',
							onSelect: () => {
								onClose();
								pick('steer');
							},
						},
						{
							label: queue,
							subtext: t({
								message: `Sends when the agent finishes. ${queueKeys}.`,
								comment:
									'Agents Settings: what Queue does. The last part is the key that does it: Enter, or ⌘/Ctrl Enter.',
							}),
							selected: value === 'queue',
							onSelect: () => {
								onClose();
								pick('queue');
							},
						},
					],
				},
			]}
			data-flx="konvo-agents.follow-up"
		/>
	);
});
